import React, { useEffect, useState } from 'react';
import { Image as ImageIcon, Upload, RotateCcw, Truck, Store, Monitor, Smartphone } from 'lucide-react';
import { apiUrl, adminFetch } from '../../api';

const DEFAULT_HERO = '/bgimGW.jpeg';
const DEFAULT_FOUNDER = '/founder.jpeg';

const DEFAULT_FULFILMENT = {
  localDeliveryEnabled: true,
  storePickupEnabled: true,
  storePickup: { address: '', hours: '', instructions: '' },
  timeSlots: [
    { value: '12-2', label: '12:00 PM - 2:00 PM', startHour: 12, enabled: true },
    { value: '2-4', label: '2:00 PM - 4:00 PM', startHour: 14, enabled: true },
    { value: '4-6', label: '4:00 PM - 6:00 PM', startHour: 16, enabled: true },
    { value: '6-8', label: '6:00 PM - 8:00 PM', startHour: 18, enabled: true },
  ],
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

function AdminSettings() {
  const [heroImage, setHeroImage] = useState('');             // '' = using default
  const [heroImageMobile, setHeroImageMobile] = useState(''); // '' = falls back to the desktop hero
  const [founderImage, setFounderImage] = useState('');       // '' = using default
  const [fulfilment, setFulfilment] = useState(DEFAULT_FULFILMENT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await adminFetch(apiUrl('/api/admin/settings'));
        const data = await res.json();
        if (data.success) {
          setHeroImage(data.settings.heroImage || '');
          setHeroImageMobile(data.settings.heroImageMobile || '');
          setFounderImage(data.settings.founderImage || '');
          if (data.settings.fulfilment) {
            setFulfilment({
              ...DEFAULT_FULFILMENT,
              ...data.settings.fulfilment,
              storePickup: { ...DEFAULT_FULFILMENT.storePickup, ...(data.settings.fulfilment.storePickup || {}) },
            });
          }
        }
      } catch { setError('Could not load settings. Is the server running?'); }
    })();
  }, []);

  const setPickup = (field, value) =>
    setFulfilment((f) => ({ ...f, storePickup: { ...f.storePickup, [field]: value } }));

  const toggleSlot = (value) =>
    setFulfilment((f) => ({
      ...f,
      timeSlots: (f.timeSlots || []).map((s) => (s.value === value ? { ...s, enabled: !s.enabled } : s)),
    }));

  // Persist a partial settings update ({ heroImage } or { founderImage }).
  const persist = async (patch, successMsg) => {
    setBusy(true); setError(''); setMsg('');
    try {
      const res = await adminFetch(apiUrl('/api/admin/settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!data.success) { setError(data.message || 'Save failed'); return; }
      setMsg(successMsg);
    } catch {
      setError('Could not save. Is the server running?');
    } finally { setBusy(false); }
  };

  const onPickHero = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setHeroImage(await fileToBase64(file));
    setMsg('');
    e.target.value = '';
  };

  const onPickHeroMobile = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setHeroImageMobile(await fileToBase64(file));
    setMsg('');
    e.target.value = '';
  };

  const onPickFounder = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setFounderImage(await fileToBase64(file));
    setMsg('');
    e.target.value = '';
  };

  const heroPreview = heroImage || DEFAULT_HERO;
  const heroMobilePreview = heroImageMobile || heroImage || DEFAULT_HERO;
  const founderPreview = founderImage || DEFAULT_FOUNDER;

  return (
    <div className="admin-settings">
      <header className="admin-main-header">
        <div>
          <h2>Site Images</h2>
          <p className="admin-muted">Upload custom images for the homepage hero (desktop and phone versions) and the About page founder photo. Leave default if you don't upload one.</p>
        </div>
      </header>

      {error && <div className="admin-error">{error}</div>}
      {msg && <div className="admin-success">{msg}</div>}

      {/* Homepage hero - desktop */}
      <div className="admin-hero-card">
        <div className="admin-hero-preview">
          <img src={heroPreview} alt="Homepage hero preview (desktop)" onError={(e) => { e.target.src = DEFAULT_HERO; }} />
          <span className="admin-hero-tag">{heroImage ? 'Custom image' : 'Default image'}</span>
        </div>

        <div className="admin-hero-actions">
          <h3 className="admin-muted" style={{ margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Monitor size={16} /> Homepage Hero - Desktop
          </h3>
          <label className="admin-btn-primary">
            <Upload size={16} /> Choose Image
            <input type="file" accept="image/*" hidden onChange={onPickHero} />
          </label>
          <button className="admin-btn-primary" onClick={() => persist({ heroImage }, 'Saved. The desktop homepage hero is now updated.')} disabled={busy}>
            {busy ? 'Saving…' : 'Save Hero Image'}
          </button>
          <button className="admin-btn-ghost dark" onClick={() => { setHeroImage(''); persist({ heroImage: '' }, 'Reset to the default hero image.'); }} disabled={busy}>
            <RotateCcw size={15} /> Reset to default
          </button>
          <p className="admin-muted sm"><ImageIcon size={13} /> Recommended: 1920×1080 px landscape (16:9), JPG under 1 MB. Shown on screens wider than 768px.</p>
          <p className="admin-muted sm">Keep the main subject near the centre - the edges get cropped on wider screens.</p>
        </div>
      </div>

      {/* Homepage hero - mobile */}
      <div className="admin-hero-card">
        <div className="admin-hero-preview mobile">
          <img src={heroMobilePreview} alt="Homepage hero preview (mobile)" onError={(e) => { e.target.src = DEFAULT_HERO; }} />
          <span className="admin-hero-tag">
            {heroImageMobile ? 'Custom image' : (heroImage ? 'Using desktop image' : 'Default image')}
          </span>
        </div>

        <div className="admin-hero-actions">
          <h3 className="admin-muted" style={{ margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Smartphone size={16} /> Homepage Hero - Phone
          </h3>
          <label className="admin-btn-primary">
            <Upload size={16} /> Choose Image
            <input type="file" accept="image/*" hidden onChange={onPickHeroMobile} />
          </label>
          <button className="admin-btn-primary" onClick={() => persist({ heroImageMobile }, 'Saved. The phone homepage hero is now updated.')} disabled={busy}>
            {busy ? 'Saving…' : 'Save Phone Hero Image'}
          </button>
          <button className="admin-btn-ghost dark" onClick={() => { setHeroImageMobile(''); persist({ heroImageMobile: '' }, 'Reset - phones will use the desktop hero image.'); }} disabled={busy}>
            <RotateCcw size={15} /> Reset to default
          </button>
          <p className="admin-muted sm"><ImageIcon size={13} /> Recommended: 1080×1080 px square (1:1), JPG under 1 MB. Shown on screens up to 768px wide.</p>
          <p className="admin-muted sm">The phone hero is a near-square band, so a portrait photo gets its top and bottom cropped - keep the subject centred. If you don't upload one, phones fall back to the desktop hero image.</p>
        </div>
      </div>

      {/* About page founder photo */}
      <div className="admin-hero-card">
        <div className="admin-hero-preview">
          <img src={founderPreview} alt="Founder preview" onError={(e) => { e.target.src = DEFAULT_FOUNDER; }} />
          <span className="admin-hero-tag">{founderImage ? 'Custom image' : 'Default image'}</span>
        </div>

        <div className="admin-hero-actions">
          <h3 className="admin-muted" style={{ margin: 0, fontWeight: 700 }}>About - Founder Photo</h3>
          <label className="admin-btn-primary">
            <Upload size={16} /> Choose Image
            <input type="file" accept="image/*" hidden onChange={onPickFounder} />
          </label>
          <button className="admin-btn-primary" onClick={() => persist({ founderImage }, 'Saved. The founder photo is now updated.')} disabled={busy}>
            {busy ? 'Saving…' : 'Save Founder Photo'}
          </button>
          <button className="admin-btn-ghost dark" onClick={() => { setFounderImage(''); persist({ founderImage: '' }, 'Reset to the default founder photo.'); }} disabled={busy}>
            <RotateCcw size={15} /> Reset to default
          </button>
          <p className="admin-muted sm"><ImageIcon size={13} /> Recommended: a portrait image (e.g. 800×1000).</p>
        </div>
      </div>

      {/* Fulfilment options */}
      <header className="admin-main-header" style={{ marginTop: 28 }}>
        <div>
          <h2>Checkout Fulfilment</h2>
          <p className="admin-muted">Enable or disable how customers receive their order. Disabled options appear greyed out and unselectable in the cart.</p>
        </div>
      </header>

      <div className="admin-fulfilment-card">
        <label className="admin-toggle-row">
          <span className="admin-toggle-label"><Truck size={16} /> Local Delivery</span>
          <input
            type="checkbox"
            checked={fulfilment.localDeliveryEnabled}
            onChange={(e) => setFulfilment((f) => ({ ...f, localDeliveryEnabled: e.target.checked }))}
          />
          <span className="admin-muted sm">{fulfilment.localDeliveryEnabled ? 'Enabled' : 'Disabled'}</span>
        </label>

        <label className="admin-toggle-row">
          <span className="admin-toggle-label"><Store size={16} /> Store Pickup</span>
          <input
            type="checkbox"
            checked={fulfilment.storePickupEnabled}
            onChange={(e) => setFulfilment((f) => ({ ...f, storePickupEnabled: e.target.checked }))}
          />
          <span className="admin-muted sm">{fulfilment.storePickupEnabled ? 'Enabled' : 'Disabled'}</span>
        </label>

        <div className="admin-pickup-fields" style={{ opacity: fulfilment.storePickupEnabled ? 1 : 0.5 }}>
          <p className="admin-muted" style={{ fontWeight: 700, margin: '4px 0 8px' }}>Store Pickup Details (shown to customers)</p>
          <div className="admin-field">
            <label className="admin-label">Pickup address</label>
            <input className="admin-input" placeholder="e.g. 12 Merchant St, Glasgow G1 1AB"
              value={fulfilment.storePickup.address}
              onChange={(e) => setPickup('address', e.target.value)}
              disabled={!fulfilment.storePickupEnabled} />
          </div>
          <div className="admin-field">
            <label className="admin-label">Pickup hours</label>
            <input className="admin-input" placeholder="e.g. Mon-Sat, 10am - 6pm"
              value={fulfilment.storePickup.hours}
              onChange={(e) => setPickup('hours', e.target.value)}
              disabled={!fulfilment.storePickupEnabled} />
          </div>
          <div className="admin-field">
            <label className="admin-label">Instructions (optional)</label>
            <textarea className="admin-input" rows={2} placeholder="e.g. Ask for your order at the counter and show your confirmation email."
              value={fulfilment.storePickup.instructions}
              onChange={(e) => setPickup('instructions', e.target.value)}
              disabled={!fulfilment.storePickupEnabled} />
          </div>
        </div>

        <div className="admin-pickup-fields">
          <p className="admin-muted" style={{ fontWeight: 700, margin: '4px 0 4px' }}>Delivery &amp; Pickup Time Slots</p>
          <p className="admin-muted sm" style={{ margin: '0 0 10px' }}>Mark which slots customers can book. At checkout, same-day slots are only offered if they start more than 1 hour from now.</p>
          {(fulfilment.timeSlots || []).map((s) => (
            <label className="admin-toggle-row" key={s.value}>
              <span className="admin-toggle-label">{s.label}</span>
              <input type="checkbox" checked={s.enabled !== false} onChange={() => toggleSlot(s.value)} />
              <span className="admin-muted sm">{s.enabled !== false ? 'Available' : 'Unavailable'}</span>
            </label>
          ))}
        </div>

        <button className="admin-btn-primary" onClick={() => persist({ fulfilment }, 'Fulfilment options saved.')} disabled={busy}>
          {busy ? 'Saving…' : 'Save Fulfilment Options'}
        </button>
      </div>
    </div>
  );
}

export default AdminSettings;
